import { Fragment } from "react";
import { EditableCell } from "./EditableCell";
import { NavActionCell } from "./NavActionCell";
import { useDeleteAssignment, useCreateAssignment } from "@/hooks/use-budget-assignments";
import { useUpdateAssignment } from "@/hooks/use-task-mutations";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PHASE_LABELS, type ProjectPhase } from "@/lib/offer-types";
import { isTaskLine } from "@/lib/billing-calculations";
import type { BudgetLineRow } from "@/hooks/use-project-detail";

interface UserOption {
  id: string;
  full_name: string;
}

const STATUS_OPTIONS = [
  { value: "not_started", label: "Ikke påbegynt" },
  { value: "in_progress", label: "Påbegynt" },
  { value: "completed", label: "Venter godkjenning" },
  { value: "approved", label: "Godkjent" },
  { value: "rejected", label: "Avvist" },
];

interface Props {
  projectId: string;
  budgetLines: BudgetLineRow[];
  users: UserOption[];
}

function phaseLabel(phase: string | null | undefined): string {
  if (!phase) return "—";
  return PHASE_LABELS[phase as ProjectPhase] ?? phase;
}

export function AdminAssignmentsTable({ projectId, budgetLines, users }: Props) {
  const updateMut = useUpdateAssignment();
  const deleteMut = useDeleteAssignment();
  const createMut = useCreateAssignment();

  const userOpts = users.map((u) => ({ value: u.id, label: u.full_name }));
  const blOpts = budgetLines
    .filter((bl) => bl.cost_type === "internal")
    .map((bl) => ({ value: bl.id, label: bl.description ?? "(uten navn)" }))
    .sort((a, b) => a.label.localeCompare(b.label, "no"));

  // Vis alle interne linjer med quantity_hours>0 (samme som Timetildeling).
  // Sortér på fase så pl/preprod/prod/post grupperes naturlig.
  const phaseOrder: Record<string, number> = {
    pl: 0, preprod: 1, production: 2, postprod: 3, stills: 4, reise: 5,
  };
  const lines = budgetLines
    .filter(isTaskLine)
    .sort((a, b) => {
      const pa = phaseOrder[a.phase ?? ""] ?? 99;
      const pb = phaseOrder[b.phase ?? ""] ?? 99;
      if (pa !== pb) return pa - pb;
      return (a.description ?? "").localeCompare(b.description ?? "");
    });

  const handleDelete = async (assignmentId: string) => {
    await deleteMut.mutateAsync({ assignmentId, projectId });
    toast("Tildeling slettet", { duration: 5000 });
  };

  const handleCreate = (budgetLineId: string) => {
    const u = userOpts[0];
    if (!u) {
      toast.error("Ingen brukere tilgjengelig");
      return;
    }
    createMut.mutate({
      projectId,
      budget_line_id: budgetLineId,
      user_id: u.value,
      hours: 0,
      label: null,
    });
  };

  if (lines.length === 0) {
    return (
      <div>
        <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">
          Tildelinger
        </div>
        <div className="p-4 text-sm text-muted-foreground italic bg-card border border-border rounded">
          Ingen interne budsjettlinjer på dette prosjektet.
        </div>
      </div>
    );
  }

  let rowIdx = 0;
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">
        Tildelinger
      </div>
      <table className="w-full border-collapse text-sm bg-card border border-border">
        <thead className="bg-muted">
          <tr>
            <th className="px-2 py-1 border border-border w-8 text-left">#</th>
            <th className="px-2 py-1 border border-border text-left">Oppgave</th>
            <th className="px-2 py-1 border border-border text-left">Label</th>
            <th className="px-2 py-1 border border-border text-left">Person</th>
            <th className="px-2 py-1 border border-border text-right w-20">Timer</th>
            <th className="px-2 py-1 border border-border text-left w-32">Status</th>
            <th className="px-2 py-1 border border-border w-8" />
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const assignments = line.assignments ?? [];
            const sumAssigned = assignments.reduce((s, a) => s + Number(a.hours ?? 0), 0);
            const sold = Number(line.quantity_hours ?? 0);
            const remaining = sold - sumAssigned;
            const isUnassigned = assignments.length === 0;
            return (
              <Fragment key={line.id}>
                <tr className="bg-muted/40 font-medium">
                  <td className="px-2 py-1 border border-border text-[10px] uppercase text-muted-foreground">
                    {phaseLabel(line.phase)}
                  </td>
                  <td colSpan={3} className="px-2 py-1 border border-border">
                    {line.description ?? "(uten navn)"}
                  </td>
                  <td className="px-2 py-1 border border-border text-right tabular-nums">
                    <span className={cn(remaining > 0 && "text-amber-700", remaining < 0 && "text-red-700")}>
                      {sumAssigned}
                    </span>
                    <span className="text-muted-foreground"> / {sold} solgt</span>
                  </td>
                  <td className="px-2 py-1 border border-border text-xs text-muted-foreground">
                    {remaining > 0 && `${remaining}t igjen`}
                    {remaining < 0 && `${Math.abs(remaining)}t over`}
                    {remaining === 0 && "fordelt"}
                  </td>
                  <NavActionCell
                    dataCol="actions"
                    onActivate={() => handleCreate(line.id)}
                    className="px-2 py-1 border border-border text-center text-primary cursor-pointer hover:bg-primary/10 font-semibold"
                    title="Legg til tildeling på denne linjen — Enter aktiverer"
                  >
                    +
                  </NavActionCell>
                </tr>
                {isUnassigned ? (
                  <tr className="bg-amber-50/40 italic text-amber-800">
                    <td className="px-2 py-1 border border-border" />
                    <td colSpan={6} className="px-2 py-1 border border-border text-xs">
                      Ikke tildelt — klikk + over for å tildele en person
                    </td>
                  </tr>
                ) : (
                  assignments.map((a) => {
                    rowIdx++;
                    return (
                      <tr key={a.id}>
                        <td className="px-2 py-1 border border-border text-muted-foreground">{rowIdx}</td>
                        <EditableCell
                          dataCol="budget_line"
                          value={line.id}
                          type="select"
                          options={blOpts}
                          onSave={(newBlId) =>
                            updateMut.mutateAsync({ assignmentId: a.id, updates: { budget_line_id: newBlId } })
                          }
                        />
                        <EditableCell
                          dataCol="label"
                          value={a.label ?? ""}
                          type="text"
                          onSave={(newLabel) =>
                            updateMut.mutateAsync({ assignmentId: a.id, updates: { label: newLabel || null } })
                          }
                        />
                        <EditableCell
                          dataCol="user"
                          value={a.user_id}
                          type="select"
                          options={userOpts}
                          onSave={(newUserId) =>
                            updateMut.mutateAsync({ assignmentId: a.id, updates: { user_id: newUserId } })
                          }
                        />
                        <EditableCell
                          dataCol="hours"
                          value={a.hours}
                          type="number"
                          align="right"
                          onSave={(newHours) =>
                            updateMut.mutateAsync({ assignmentId: a.id, updates: { hours: newHours } })
                          }
                        />
                        <EditableCell
                          dataCol="status"
                          value={a.task_status}
                          type="select"
                          options={STATUS_OPTIONS}
                          onSave={(newStatus) =>
                            updateMut.mutateAsync({ assignmentId: a.id, updates: { task_status: newStatus } })
                          }
                        />
                        <NavActionCell
                          dataCol="actions"
                          onActivate={() => handleDelete(a.id)}
                          className="px-2 py-1 border border-border text-center text-destructive cursor-pointer hover:bg-red-50"
                          title="Slett tildeling — Enter aktiverer"
                        >
                          ×
                        </NavActionCell>
                      </tr>
                    );
                  })
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
